import React, { useState, useEffect, useRef } from 'react';
import { VictoryPie, VictoryLabel } from 'victory';
import { emotionColors, emotionOrder } from '../utils';

const PieGraph = ({ pieGraphData, tally }) => {
  const [activeEmotion, setActiveEmotion] = useState(null);
  const [tooltipEmotion, setTooltipEmotion] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const isEmptyState = pieGraphData.every(d => d.count === 0)
  
  const activeEmotionData = pieGraphData.find(d => d.name === activeEmotion)
  const count = activeEmotionData?.count || 0

  const studentPieData = emotionOrder
      .map((name, index) => {
        const match = pieGraphData.find(d => d.name === name)
        if (!match) return null
        const count = match.count || 0
        const percentage = match.currentPercent || 0
        const color = emotionColors[name] || '#ccc'
        return {
          x: name,
          y: tally > 0 ? count : 1,
          index,
          percentage,
          color
        }
      })
      .filter(Boolean)
    ;
  
  useEffect(() => {
    if (studentPieData.length > 0 && !activeEmotion) {
      let maxPercentage = -Infinity;
      let dominantEmotion = null;
      
      studentPieData.forEach(datum => {
        if (datum.percentage > maxPercentage) {
          maxPercentage = datum.percentage;
          dominantEmotion = datum.x;
        }
      });

      if (maxPercentage > 0) {
        setActiveEmotion(dominantEmotion);
      }
    return;
    }
  }, [studentPieData, activeEmotion]);

  const handleSliceClick = (evt, { datum }) => {
    const container = evt.currentTarget.closest('.pie-container');
    const containerRect = container?.getBoundingClientRect();

    if (!containerRect) return;

    const { clientX, clientY } = evt.nativeEvent;
    const localY = clientY - containerRect.top;

    const sliceRect = evt.target.getBoundingClientRect();
    const centerX = sliceRect.left + sliceRect.width / 2;
    const isRightSide = clientX > centerX;
    const localX = isRightSide
      ? centerX - containerRect.left + 160
      : centerX - containerRect.left - 160;

    const wasActive = tooltipEmotion === datum.x;

    setActiveEmotion(datum.x);
    setTooltipEmotion(wasActive ? null : datum.x);

    setTooltipPosition({ x: localX, y: localY });
  };

  const pieRef = useRef(null); 

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pieRef.current && !pieRef.current.contains(e.target)) {
        setTooltipEmotion(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="w-auto h-[430px] m-auto relative pie-container">
      <VictoryPie
        data={studentPieData}
        innerRadius={({ datum }) => activeEmotion === datum.x ? 80 : 100}
        radius={120}
        padAngle={2} 
        startAngle={-60}
        endAngle={300}
        labels={({ datum }) => {
          if (!isEmptyState && datum.percentage === 0) return '';
          return `${datum.x} ${datum.percentage}%`;
        }}
        
        labelPosition={"centroid"}
        labelPlacement={"vertical"}
  
        labelRadius={({ datum }) => {
          const baseRadius = 160;
          const angle = (datum.startAngle + datum.endAngle) / 2;
          if (isEmptyState) {
            if ((angle >= -60 && angle <= -20) || (angle >= 20 && angle <= 60) ) {
              return baseRadius + 20;
            } else if (angle >= 140 && angle <= 220) {
              return baseRadius + 10;
            }
            return baseRadius;
          }
          if (angle >= 200 && angle <= 240) {
            return baseRadius - 5;
          } else if (angle >= 240 && angle <= 300) {
            return baseRadius - 20;
          } else if (angle >= -60 && angle < -45) {
            return baseRadius + 30;
          }else if (angle >= -45 && angle < 30) {
            return baseRadius - 12;
          } else if (angle >= 50 && angle < 120) {
            return baseRadius - 22;
          }
          return baseRadius;
        }}

        labelComponent={
          <VictoryLabel
            verticalAnchor="middle"
            dy={0}
            style={[
              {
                fill: ({ datum }) => emotionColors[datum.x] || '#cccccc',
                fontSize: 14,
                fontWeight: 400,
                textTransform: 'capitalize'
              }
            ]}
          />
        }
        style={{
          data: {
            fill: ({ datum }) => datum.color
          }
        }}
        events={[
          {
            target: 'data',
            eventHandlers: {
              onClick: handleSliceClick
            }
          }
        ]}
      />

      {activeEmotion ? (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none flex flex-col items-center justify-center">
          <div className="capitalize text-[24px] font-bold text-700">
            {activeEmotion}
          </div>
          <div className="w-[80%] mx-auto my-2 border-t border-gray-400" />
            <div className="text-[17px] text-400 font-medium">
             {count} {count === 1 ? 'Time' : 'Times'}
            </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default PieGraph;